import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, Save, Eye, Plus, Trash2, GripVertical, Settings2, X,
  Type, AlignLeft, Minus, FileText, Square, ChevronDown, ChevronRight
} from 'lucide-react';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../../components/UnsavedChangesModal';
import VisibilityRulesEditor from '../../components/admin/VisibilityRulesEditor';

const WIDGET_PALETTE = [
  { type: 'section_header', label: 'Section Header', icon: AlignLeft, description: 'Add a heading' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal line' },
  { type: 'static_text', label: 'Info Text', icon: FileText, description: 'Static text block' },
  { type: 'spacer', label: 'Spacer', icon: Square, description: 'Empty space' },
];

const SPAN_OPTIONS = [
  { value: 3, label: '3 (Quarter)' },
  { value: 4, label: '4 (Third)' },
  { value: 6, label: '6 (Half)' },
  { value: 8, label: '8 (Two-thirds)' },
  { value: 12, label: '12 (Full width)' },
];

export default function ScreenBuilder() {
  const { recordTypeId, screenId } = useParams();
  const navigate = useNavigate();
  const [screen, setScreen] = useState(null);
  const [fields, setFields] = useState([]);
  const [layout, setLayout] = useState({ sections: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const unsavedBlocker = useUnsavedChanges(dirty);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [fieldSearch, setFieldSearch] = useState('');
  const isAddingRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await adminApi.getScreen(screenId);
        setScreen(res.data.screen);
        setFields(res.data.fields);
        setLayout(res.data.screen.layout && res.data.screen.layout.sections ? res.data.screen.layout : { sections: [] });
      } catch (err) {
        toast.error('Failed to load screen');
        navigate(`/admin/record-types/${recordTypeId}/screens`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [screenId]);

  const updateLayout = useCallback((newLayout) => {
    setLayout(newLayout);
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateScreenLayout(screenId, layout);
      setDirty(false);
      toast.success('Layout saved');
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      collapsible: true,
      collapsed: false,
      columns: 12,
      widgets: [],
    };
    const newLayout = { ...layout, sections: [...layout.sections, newSection] };
    updateLayout(newLayout);
    setActiveSectionIndex(newLayout.sections.length - 1);
  };

  const updateSection = (sectionIndex, updates) => {
    const sections = [...layout.sections];
    sections[sectionIndex] = { ...sections[sectionIndex], ...updates };
    updateLayout({ ...layout, sections });
  };

  const deleteSection = (sectionIndex) => {
    if (!window.confirm('Delete this section and all its widgets?')) return;
    const sections = layout.sections.filter((_, i) => i !== sectionIndex);
    updateLayout({ ...layout, sections });
    setSelectedWidget(null);
    if (activeSectionIndex >= sections.length) setActiveSectionIndex(Math.max(0, sections.length - 1));
  };

  const addWidgetToSection = (sectionIndex, widgetType, fieldId) => {
    // Guard: prevent rapid-fire duplicate additions from click-and-hold
    if (isAddingRef.current) return;
    isAddingRef.current = true;
    setTimeout(() => { isAddingRef.current = false; }, 300);

    // Guard: prevent duplicate field widgets
    if (widgetType === 'field' && fieldId) {
      const alreadyExists = layout.sections.some(s =>
        s.widgets.some(w => w.type === 'field' && w.config?.field_id === fieldId)
      );
      if (alreadyExists) return;
    }

    const sections = [...layout.sections];
    const fieldDef = fieldId ? fields.find(f => f.id === fieldId) : null;
    const fullWidthTypes = ['REPEATER', 'REPEATER_SECTION', 'ADDRESS', 'RICH_TEXT', 'TEXTAREA'];
    const autoFullWidth = widgetType === 'divider' || widgetType === 'section_header' || widgetType === 'static_text'
      || (widgetType === 'field' && fieldDef && fullWidthTypes.includes(fieldDef.field_type));
    const widget = {
      id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      type: widgetType,
      col_span: autoFullWidth ? 12 : 6,
      config: widgetType === 'field'
        ? { field_id: fieldId || null }
        : widgetType === 'section_header'
        ? { text: 'Header' }
        : widgetType === 'static_text'
        ? { text: 'Info text here' }
        : widgetType === 'spacer'
        ? { height: 24 }
        : {},
    };
    sections[sectionIndex] = {
      ...sections[sectionIndex],
      widgets: [...sections[sectionIndex].widgets, widget],
    };
    updateLayout({ ...layout, sections });
    setSelectedWidget({ sectionIndex, widgetIndex: sections[sectionIndex].widgets.length - 1 });
    setActiveSectionIndex(sectionIndex);
    // Auto-scroll to the new widget
    setTimeout(() => {
      const el = document.getElementById(`widget-${widget.id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handlePaletteClick = (widgetType) => {
    if (layout.sections.length === 0) { toast.error('Add a section first'); return; }
    addWidgetToSection(activeSectionIndex, widgetType, null);
  };

  const handleFieldClick = (fieldId) => {
    if (layout.sections.length === 0) { toast.error('Add a section first'); return; }
    addWidgetToSection(activeSectionIndex, 'field', fieldId);
  };

  const updateWidget = (sectionIndex, widgetIndex, updates) => {
    const sections = [...layout.sections];
    const widgets = [...sections[sectionIndex].widgets];
    widgets[widgetIndex] = { ...widgets[widgetIndex], ...updates };
    sections[sectionIndex] = { ...sections[sectionIndex], widgets };
    updateLayout({ ...layout, sections });
  };

  const deleteWidget = (sectionIndex, widgetIndex) => {
    const sections = [...layout.sections];
    sections[sectionIndex] = {
      ...sections[sectionIndex],
      widgets: sections[sectionIndex].widgets.filter((_, i) => i !== widgetIndex),
    };
    updateLayout({ ...layout, sections });
    setSelectedWidget(null);
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = layout.sections.findIndex(s => s.id === active.id);
    const newIndex = layout.sections.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    updateLayout({ ...layout, sections: arrayMove(layout.sections, oldIndex, newIndex) });
    setActiveSectionIndex(newIndex);
  };

  const usedFieldIds = (layout?.sections || []).flatMap(s =>
    s.widgets.filter(w => w.type === 'field' && w.config?.field_id).map(w => w.config.field_id)
  );
  const availableFields = fields.filter(f => !usedFieldIds.includes(f.id));

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  const selectedWidgetData = selectedWidget
    ? layout.sections[selectedWidget.sectionIndex]?.widgets[selectedWidget.widgetIndex]
    : null;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/admin/record-types/${recordTypeId}/screens`)} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{screen?.name}</h1>
            <p className="text-xs text-gray-500">Drag widgets to reorder - Click to configure</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border ${previewMode ? 'bg-amber-50 border-amber-200 text-amber-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Eye className="h-4 w-4" /> {previewMode ? 'Exit Preview' : 'Preview'}
          </button>
          {dirty && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Layout'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {!previewMode && (
          <div className="w-56 bg-gray-50 border-r border-gray-200 overflow-y-auto shrink-0">
            <div className="p-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Widgets</p>
              <p className="text-[10px] text-gray-400 mb-2">Click to add to active section</p>
              <div className="space-y-1">
                {WIDGET_PALETTE.map((item) => (
                  <div key={item.type}
                    onClick={() => handlePaletteClick(item.type)}
                    className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 cursor-pointer hover:border-brand-400 hover:bg-brand-50 hover:shadow-sm active:scale-[0.98] transition-all">
                    <item.icon className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-6 mb-2">
                Available Fields ({availableFields.length})
              </p>
              <p className="text-[10px] text-gray-400 mb-2">Click to add as form field</p>
              <input
                type="text"
                value={fieldSearch}
                onChange={e => setFieldSearch(e.target.value)}
                placeholder="Search fields..."
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs mb-2 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-400"
              />
              {availableFields.filter(f => !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.field_type.toLowerCase().includes(fieldSearch.toLowerCase()) || (f.name || '').toLowerCase().includes(fieldSearch.toLowerCase())).length === 0 ? (
                <p className="text-xs text-gray-400 px-2">{fieldSearch ? 'No matching fields' : 'All fields placed'}</p>
              ) : (
                <div className="space-y-1">
                  {availableFields.filter(f => !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.field_type.toLowerCase().includes(fieldSearch.toLowerCase()) || (f.name || '').toLowerCase().includes(fieldSearch.toLowerCase())).map((field) => (
                    <div key={field.id}
                      onClick={() => handleFieldClick(field.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-xs cursor-pointer hover:border-brand-400 hover:bg-brand-50 hover:shadow-sm active:scale-[0.98] transition-all">
                      <span className="text-gray-500 font-mono text-[10px] shrink-0">{field.field_type}</span>
                      <span className="text-gray-700 font-medium truncate">{field.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          {previewMode ? (
            <PreviewPanel layout={layout} fields={fields} previewData={previewData} setPreviewData={setPreviewData} />
          ) : (
            <div className="max-w-4xl mx-auto space-y-4">
              {layout.sections.length === 0 ? (
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
                  <p className="text-gray-500 mb-4">No sections yet. Add a section to start building your screen.</p>
                  <button onClick={addSection}
                    className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
                    <Plus className="h-4 w-4" /> Add Section
                  </button>
                </div>
              ) : (
                <>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                    <SortableContext items={layout.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                      {layout.sections.map((section, sectionIndex) => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          sectionIndex={sectionIndex}
                          fields={fields}
                          isActive={sectionIndex === activeSectionIndex}
                          selectedWidget={selectedWidget}
                          sensors={sensors}
                          onActivate={() => setActiveSectionIndex(sectionIndex)}
                          onSelectWidget={setSelectedWidget}
                          onUpdateSection={(updates) => updateSection(sectionIndex, updates)}
                          onDeleteSection={() => deleteSection(sectionIndex)}
                          onAddWidget={(type, fieldId) => addWidgetToSection(sectionIndex, type, fieldId)}
                          onDeleteWidget={(wi) => deleteWidget(sectionIndex, wi)}
                          onUpdateLayout={updateLayout}
                          layout={layout}
                          availableFields={availableFields}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  <button onClick={addSection}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
                    <Plus className="h-4 w-4 inline mr-1" /> Add Section
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {!previewMode && (
          <div className="w-72 bg-white border-l border-gray-200 overflow-y-auto shrink-0">
            {selectedWidgetData ? (
              <WidgetConfig
                widget={selectedWidgetData}
                fields={fields}
                onChange={(updates) => updateWidget(selectedWidget.sectionIndex, selectedWidget.widgetIndex, updates)}
                onClose={() => setSelectedWidget(null)}
              />
            ) : (
              <div className="p-4 text-center text-gray-400 text-sm mt-12">
                <Settings2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>Select a widget to configure it</p>
              </div>
            )}
          </div>
        )}
      </div>
      <UnsavedChangesModal blocker={unsavedBlocker} />
    </div>
  );
}

function SortableSection({ section, sectionIndex, fields, isActive, selectedWidget, sensors,
  onActivate, onSelectWidget, onUpdateSection, onDeleteSection, onAddWidget, onDeleteWidget,
  onUpdateLayout, layout, availableFields }) {

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleWidgetDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = section.widgets.findIndex(w => w.id === active.id);
    const newIndex = section.widgets.findIndex(w => w.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const sections = [...layout.sections];
    sections[sectionIndex] = {
      ...sections[sectionIndex],
      widgets: arrayMove(sections[sectionIndex].widgets, oldIndex, newIndex),
    };
    onUpdateLayout({ ...layout, sections });
  };

  return (
    <div ref={setNodeRef} style={style}
      className={`bg-white rounded-xl border-2 transition-colors ${isActive ? 'border-brand-300 shadow-sm' : 'border-gray-200'}`}
      onClick={onActivate}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-[10px]">
        <div className="flex items-center gap-2">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-gray-200 rounded">
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          {editingTitle ? (
            <input type="text" value={section.title || ''}
              onChange={e => onUpdateSection({ title: e.target.value })}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={e => e.key === 'Enter' && setEditingTitle(false)}
              autoFocus
              className="text-sm font-semibold text-gray-700 border border-brand-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          ) : (
            <span className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-brand-600"
              onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}>
              {section.title || 'Untitled Section'}
            </span>
          )}
          {isActive && <span className="text-[10px] bg-brand-100 text-brand-600 px-1.5 py-0.5 rounded font-medium">active</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}
            className="px-2 py-1 text-xs rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200"
            title={collapsed ? 'Expand section' : 'Collapse section'}>
            {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onUpdateSection({ collapsible: !section.collapsible }); }}
            className={`px-2 py-1 text-[10px] rounded font-medium ${section.collapsible ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}
            title={section.collapsible ? 'Section is collapsible by users — click to disable' : 'Section is not collapsible by users — click to enable'}>
            {section.collapsible ? 'Collapsible' : 'Fixed'}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDeleteSection(); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete section">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {collapsed ? (
        <div className="px-4 py-2 text-xs text-gray-400 italic">{section.widgets.length} widget{section.widgets.length !== 1 ? 's' : ''} (collapsed)</div>
      ) : (
      <div className="p-4 min-h-[60px]">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleWidgetDragEnd}>
          <SortableContext items={section.widgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px', alignItems: 'start' }}>
              {section.widgets.map((widget, widgetIndex) => (
                <SortableWidget
                  key={widget.id}
                  widget={widget}
                  widgetIndex={widgetIndex}
                  sectionIndex={sectionIndex}
                  fields={fields}
                  isSelected={selectedWidget?.sectionIndex === sectionIndex && selectedWidget?.widgetIndex === widgetIndex}
                  onSelect={() => onSelectWidget({ sectionIndex, widgetIndex })}
                  onDelete={() => onDeleteWidget(widgetIndex)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="relative mt-2">
          <button onClick={(e) => { e.stopPropagation(); setShowAddMenu(!showAddMenu); }}
            className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:border-brand-300 hover:text-brand-600 transition-colors">
            <Plus className="h-3.5 w-3.5 inline mr-1" /> Add Widget
          </button>
          {showAddMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 w-64 py-1">
              {WIDGET_PALETTE.map((item) => (
                <button key={item.type}
                  onClick={(e) => { e.stopPropagation(); onAddWidget(item.type, null); setShowAddMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <item.icon className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              ))}
              {availableFields.length > 0 && (
                <>
                  <hr className="my-1" />
                  <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase">Quick Add Field</p>
                  <div className="max-h-40 overflow-y-auto">
                    {availableFields.map((field) => (
                      <button key={field.id}
                        onClick={(e) => { e.stopPropagation(); onAddWidget('field', field.id); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                        <span className="font-mono text-[10px] text-gray-400 shrink-0">{field.field_type}</span>
                        <span className="truncate">{field.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function SortableWidget({ widget, widgetIndex, sectionIndex, fields, isSelected, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const field = widget.type === 'field' ? fields.find(f => f.id === widget.config?.field_id) : null;
  const isUnassigned = widget.type === 'field' && !field;
  const style = {
    gridColumn: `span ${widget.col_span || 12}`,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} id={`widget-${widget.id}`}
      className={`relative group rounded-lg border-2 p-3 cursor-pointer transition-all ${
        isSelected ? 'border-brand-500 bg-brand-50 shadow-sm'
        : isUnassigned ? 'border-dashed border-amber-400 bg-amber-50 hover:border-amber-500'
        : 'border-gray-200 hover:border-brand-300 bg-white'
      } ${isDragging ? 'shadow-lg z-10' : ''}`}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}>
      <div {...attributes} {...listeners}
        className="absolute top-1 left-1 cursor-grab active:cursor-grabbing p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 rounded"
        onClick={(e) => e.stopPropagation()}>
        <GripVertical className="h-3 w-3 text-gray-400" />
      </div>
      <div className="text-xs pl-4">
        {widget.type === 'field' ? (
          field ? (
            <div>
              <span className="font-medium text-gray-700">{widget.config?.label_override || field.label}</span>
              <span className="text-gray-400 ml-1 text-[10px]">{field.field_type}</span>
              {field.is_required && <span className="text-red-400 ml-1">*</span>}
              {['REPEATER', 'REPEATER_SECTION', 'ADDRESS'].includes(field.field_type) && (widget.col_span || 12) < 12 && (
                <div className="text-[10px] text-amber-500 mt-0.5">⚠️ Recommended: full width (12)</div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 font-medium">No field assigned</span>
              <span className="text-amber-500 text-[10px]">← Select a field in the config panel</span>
            </div>
          )
        ) : widget.type === 'section_header' ? (
          <span className="font-semibold text-gray-600">{widget.config?.text || 'Header'}</span>
        ) : widget.type === 'divider' ? (
          <div className="py-1"><hr className="border-gray-300" /></div>
        ) : widget.type === 'static_text' ? (
          <span className="text-blue-600">{(widget.config?.text || '').substring(0, 50)}</span>
        ) : widget.type === 'spacer' ? (
          <span className="text-gray-300">Spacer ({widget.config?.height || 24}px)</span>
        ) : (
          <span className="text-gray-400">{widget.type}</span>
        )}
      </div>
      <div className="absolute top-1 right-1 text-[9px] text-gray-300 font-mono">{widget.col_span || 12}/12</div>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute -top-2 -right-2 hidden group-hover:flex h-5 w-5 items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm text-gray-400 hover:text-red-600 hover:border-red-200 z-10">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function WidgetConfig({ widget, fields, onChange, onClose }) {
  return (
    <div>
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">Widget Config</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{widget.type}</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Width</label>
          <select value={widget.col_span || 12}
            onChange={e => onChange({ col_span: parseInt(e.target.value) })}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
            {SPAN_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {widget.type === 'field' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Field</label>
              <select value={widget.config?.field_id || ''}
                onChange={e => onChange({ config: { ...widget.config, field_id: e.target.value || null } })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                <option value="">Select field...</option>
                {fields.map(f => (
                  <option key={f.id} value={f.id}>{f.label} ({f.field_type})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Label Override</label>
              <input type="text" value={widget.config?.label_override || ''}
                onChange={e => onChange({ config: { ...widget.config, label_override: e.target.value } })}
                placeholder="Use default label"
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={widget.config?.read_only || false}
                  onChange={e => onChange({ config: { ...widget.config, read_only: e.target.checked } })}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600" />
                <span className="text-xs font-medium text-gray-600">Read-Only on this screen</span>
              </label>
              <p className="text-[10px] text-gray-400 mt-1 ml-6">Field is visible but not editable. Useful for showing values entered on another screen.</p>
            </div>
          </>
        )}
        {widget.type === 'section_header' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Heading Text</label>
              <input type="text" value={widget.config?.text || ''}
                onChange={e => onChange({ config: { ...widget.config, text: e.target.value } })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Subtitle</label>
              <input type="text" value={widget.config?.subtitle || ''}
                onChange={e => onChange({ config: { ...widget.config, subtitle: e.target.value } })}
                placeholder="Optional"
                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </>
        )}
        {widget.type === 'static_text' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Text Content</label>
            <textarea value={widget.config?.text || ''}
              onChange={e => onChange({ config: { ...widget.config, text: e.target.value } })}
              rows={3}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
        )}
        {widget.type === 'spacer' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Height (px)</label>
            <input type="number" value={widget.config?.height || 24}
              onChange={e => onChange({ config: { ...widget.config, height: parseInt(e.target.value) || 24 } })}
              min={8} max={200}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
        )}
        <VisibilityRulesEditor rules={widget.visibility_rules} fields={fields}
          onChange={(rules) => onChange({ visibility_rules: rules })} />
      </div>
    </div>
  );
}

// VisibilityRulesEditor extracted to components/admin/VisibilityRulesEditor.js
// so FieldManager can reuse it. ScreenBuilder still uses it with default
// fieldKey='field_id' (widgets reference fields by UUID).

function PreviewPanel({ layout, fields, previewData, setPreviewData }) {
  const ScreenRenderer = require('../../components/screens/ScreenRenderer').default;
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-700">
        Preview Mode - enter sample data to test your layout and visibility rules
      </div>
      <ScreenRenderer
        layout={layout}
        fields={fields}
        fieldData={previewData}
        onChange={(fieldId, value) => setPreviewData(prev => ({ ...prev, [fieldId]: value }))}
        errors={{}}
        readOnly={false}
      />
    </div>
  );
}
